import { useState, useEffect } from "react";
import "@/App.css";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import LibraryPage from "./pages/LibraryPage";
import SearchPage from "./pages/SearchPage";
import ArticlePage from "./pages/ArticlePage";
import FileDetailPage from "./pages/FileDetailPage";
import AIChatPage from "./pages/AIChatPage";
import ProjectsPage from "./pages/ProjectsPage";
import StoriesPage from "./pages/StoriesPage";
import SettingsPage from "./pages/SettingsPage";
import { Sidebar } from "./components/Sidebar";
import { InstallPrompt } from "./components/InstallPrompt";
import { AIConsentProvider } from "./components/AIConsentDialog";
import { Toaster } from "./components/ui/sonner";
import { authAPI } from "./lib/api";

// Register Service Worker (for offline/PWA support)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => console.log('SW registered:', reg.scope))
      .catch((err) => console.log('SW registration failed:', err));
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [pageData, setPageData] = useState({});
  const [authChecked, setAuthChecked] = useState(false);
  const [verifyToken, setVerifyToken] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    // Check for email-verification or password-reset deep links first.
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("verify");
      const r = params.get("reset");
      if (v) setVerifyToken(v);
      if (r) setResetToken(r);
    } catch {}

    const token = localStorage.getItem("archiva_token");
    const storedUser = localStorage.getItem("archiva_user");
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem("archiva_token");
        localStorage.removeItem("archiva_user");
      }
    }
    setAuthChecked(true);
  }, []);

  const clearQueryParam = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("verify");
      url.searchParams.delete("reset");
      window.history.replaceState({}, "", url.pathname + url.search);
    } catch {}
  };

  const handleVerifyDone = () => {
    setVerifyToken(null);
    clearQueryParam();
    // If the verify endpoint returned a token, pick up the now-authed user.
    const storedUser = localStorage.getItem("archiva_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setCurrentPage("dashboard");
      } catch {}
    } else {
      setShowAuth(true);
    }
  };

  const handleResetDone = () => {
    setResetToken(null);
    clearQueryParam();
    const storedUser = localStorage.getItem("archiva_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setCurrentPage("dashboard");
      } catch {}
    } else {
      setShowAuth(true);
    }
  };

  const handleAuth = (userData) => {
    setUser(userData);
    setCurrentPage("dashboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("archiva_token");
    localStorage.removeItem("archiva_user");
    setUser(null);
    setCurrentPage("dashboard");
    setPageData({});
  };

  const handleNavigate = (page, data = {}) => {
    if (page === currentPage) {
      data._resetKey = Date.now();
    }
    setCurrentPage(page);
    setPageData(data);
    window.scrollTo(0, 0);
  };

  if (!authChecked) return null;

  // Deep link: email verification
  if (verifyToken) {
    return (
      <>
        <VerifyEmailPage token={verifyToken} onDone={handleVerifyDone} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Deep link: password reset
  if (resetToken) {
    return (
      <>
        <ResetPasswordPage token={resetToken} onDone={handleResetDone} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (!user) {
    if (showAuth) {
      return (
        <>
          <AuthPage onAuth={handleAuth} onBack={() => setShowAuth(false)} />
          <InstallPrompt />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    return (
      <>
        <LandingPage onGetStarted={() => setShowAuth(true)} />
        <InstallPrompt />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard":
        return <DashboardPage onNavigate={handleNavigate} />;
      case "upload":
        return <UploadPage />;
      case "library":
        return <LibraryPage onNavigate={handleNavigate} initialTag={pageData.tag} />;
      case "search":
        return <SearchPage onNavigate={handleNavigate} initialQuery={pageData.query} />;
      case "article":
        return <ArticlePage article={pageData.article} query={pageData.query} fileIds={pageData.fileIds} onNavigate={handleNavigate} />;
      case "file-detail":
        return <FileDetailPage fileId={pageData.fileId} onNavigate={handleNavigate} />;
      case "ai-chat":
        return <AIChatPage />;
      case "projects":
        return <ProjectsPage key={pageData._resetKey || 'projects'} />;
      case "stories":
        return <StoriesPage key={pageData._resetKey || 'stories'} />;
      case "settings":
        return <SettingsPage user={user} onAccountDeleted={handleLogout} />;
      default:
        return <DashboardPage onNavigate={handleNavigate} />;
    }
  };

  return (
    <AIConsentProvider>
      <div className="flex min-h-screen bg-background" data-testid="app-container">
        <Sidebar
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          user={user}
        />
        <main className="flex-1 md:ml-64 md:pt-0 p-3 md:p-8 overflow-x-hidden mobile-main-padding">
          {renderPage()}
        </main>
        <InstallPrompt />
        <Toaster position="top-right" richColors />
      </div>
    </AIConsentProvider>
  );
}

export default App;
